/**
 * NotificationBell Component
 * Displays a bell icon with notification count and dropdown panel
 * Shows: ticket responses, system updates, changelogs, outages
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, MessageSquare, Megaphone, AlertTriangle, Sparkles, CheckCheck, X } from 'lucide-react'
import { announcementsService, type AnnouncementLinkedSet, type AnnouncementSeverity } from '../../services/announcements.service'
import { useNotifications } from '../../hooks/useNotifications'
import { SetAnnouncementModal } from '../modals'
import { useAuth } from '../../contexts/AuthContext'
import styles from './NotificationBell.module.css'

export type NotificationType =
  | 'ticket_response'
  | 'changelog'
  | 'announcement'
  | 'outage'
  | 'dashboard_alert'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  link?: string
  gameId?: string | null
  linkedSet?: AnnouncementLinkedSet | null
  read: boolean
  createdAt: string
  severity?: AnnouncementSeverity
  /** Dashboard-sourced rows only — controls dismiss button visibility */
  dashboardDismissible?: boolean
}

const ANNOUNCEMENT_ID_PREFIX = 'announcement-'

/** Prefix for synthetic notifications built from `useDashboardAlerts` (session-dismissed). */
const DASHBOARD_ALERT_NOTIF_PREFIX = 'dash-alert:'

const announcementUuidFromNotificationId = (notificationId: string): string | null => {
  if (!notificationId.startsWith(ANNOUNCEMENT_ID_PREFIX)) return null
  const uuid = notificationId.slice(ANNOUNCEMENT_ID_PREFIX.length).trim()
  return uuid.length > 0 ? uuid : null
}

function isClearableServerAnnouncement(notification: Notification): boolean {
  return notification.type === 'announcement' || notification.type === 'outage'
}

/** Legacy keys — migrated once to server-backed UserAnnouncementState */
const LEGACY_READ_NOTIFICATIONS_KEY = 'tcgeasy_read_notifications'
const LEGACY_HIDDEN_NOTIFICATIONS_KEY = 'tcgeasy_hidden_notifications'
const LEGACY_STATE_MIGRATED_KEY = 'tcgeasy_announcement_state_migrated_v1'

const readStoredIds = (storageKey: string): string[] => {
  try {
    const raw = localStorage.getItem(storageKey)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      : []
  } catch {
    return []
  }
}

export const NotificationBell = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const {
    announcements,
    unreadTicketCount,
    loading: feedLoading,
    refetch,
    bellDashboardAlerts,
    dismissDashboardAlert,
  } = useNotifications()
  const loading = feedLoading
  const [activeAnnouncementSet, setActiveAnnouncementSet] = useState<{ linkedSet: AnnouncementLinkedSet; message?: string | null } | null>(null)
  const [liveRegionText, setLiveRegionText] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const bellButtonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const loadNotificationsRef = useRef<() => Promise<unknown>>(async () => {})
  const prevAnnouncementsUnreadRef = useRef<number | null>(null)
  /** Server-backed dismiss can lag one GET behind POST — hide UUIDs until `userState.dismissedAt` confirms. */
  const pendingDismissedAnnouncementIdsRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    for (const a of announcements) {
      if (a.userState?.dismissedAt) {
        pendingDismissedAnnouncementIdsRef.current.delete(a.id)
      }
    }
  }, [announcements])

  const mapAnnouncementsToNotifications = useCallback((rows: typeof announcements) => {
    const pending = pendingDismissedAnnouncementIdsRef.current
    const announcementNotifs: Notification[] = rows
      .filter((a) => !a.userState?.dismissedAt && !pending.has(a.id))
      .map((a) => ({
        id: `${ANNOUNCEMENT_ID_PREFIX}${a.id}`,
        type: (a.severity === 'critical' ? 'outage' : 'announcement') as NotificationType,
        title: a.title,
        message: a.message,
        gameId: a.gameId ?? null,
        linkedSet: a.linkedSet ?? null,
        read: Boolean(a.userState?.readAt),
        createdAt: a.startsAt || a.createdAt,
        severity: a.severity,
      }))
    return announcementNotifs
  }, [])

  const markLocallyDismissedAnnouncement = useCallback((notificationId: string) => {
    const uuid = announcementUuidFromNotificationId(notificationId)
    if (uuid) {
      pendingDismissedAnnouncementIdsRef.current.add(uuid)
    }
  }, [])

  const mapDashboardRowsToNotifications = useCallback((rows: typeof bellDashboardAlerts): Notification[] => {
    return rows.map((d) => ({
      id: `${DASHBOARD_ALERT_NOTIF_PREFIX}${d.id}`,
      type: 'dashboard_alert' as NotificationType,
      title: d.title,
      message: d.body,
      link: d.cta.href,
      read: true,
      createdAt: new Date().toISOString(),
      severity: d.severity,
      dashboardDismissible: d.dismissible,
    }))
  }, [])

  const notificationSeverityRank = useCallback((n: Notification): number => {
    const s = n.severity
    if (s === 'critical') return 0
    if (s === 'warning') return 1
    return 2
  }, [])

  useEffect(() => {
    const ann = mapAnnouncementsToNotifications(announcements)
    const dash = mapDashboardRowsToNotifications(bellDashboardAlerts)
    const merged = [...ann, ...dash].sort(
      (a, b) => notificationSeverityRank(a) - notificationSeverityRank(b) || a.title.localeCompare(b.title),
    )
    setNotifications(merged)
  }, [
    announcements,
    bellDashboardAlerts,
    mapAnnouncementsToNotifications,
    mapDashboardRowsToNotifications,
    notificationSeverityRank,
  ])

  loadNotificationsRef.current = refetch

  const isSuperAdmin = user?.role === 'super_admin'

  const announcementsUnreadCount = notifications.filter((n) => !n.read).length
  const badgeTotal = announcementsUnreadCount + unreadTicketCount
  const clearableServerAnnouncements = notifications.filter(isClearableServerAnnouncement)

  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) refetch().catch(() => {})
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [refetch])

  /** One-time migration from localStorage bell state to server (per browser profile). */
  useEffect(() => {
    if (!user?.id || typeof localStorage === 'undefined') return
    if (localStorage.getItem(LEGACY_STATE_MIGRATED_KEY)) return

    let cancelled = false
    void (async () => {
      try {
        const readIds = readStoredIds(LEGACY_READ_NOTIFICATIONS_KEY)
        const hiddenIds = readStoredIds(LEGACY_HIDDEN_NOTIFICATIONS_KEY)
        const hiddenUuids = new Set(
          hiddenIds.map(announcementUuidFromNotificationId).filter((x): x is string => Boolean(x))
        )
        const readUuids = readIds
          .map(announcementUuidFromNotificationId)
          .filter((x): x is string => Boolean(x))
          .filter((u) => !hiddenUuids.has(u))

        if (cancelled) return

        if (hiddenUuids.size > 0) {
          await announcementsService.updateAnnouncementStates({
            announcementIds: [...hiddenUuids],
            read: true,
            dismissed: true,
          })
        }
        if (readUuids.length > 0) {
          await announcementsService.updateAnnouncementStates({
            announcementIds: readUuids,
            read: true,
          })
        }

        if (cancelled) return

        localStorage.removeItem(LEGACY_READ_NOTIFICATIONS_KEY)
        localStorage.removeItem(LEGACY_HIDDEN_NOTIFICATIONS_KEY)
        localStorage.setItem(LEGACY_STATE_MIGRATED_KEY, '1')
      } catch (e) {
        console.error('Failed to migrate announcement state from localStorage:', e)
      } finally {
        if (!cancelled) {
          void loadNotificationsRef.current()
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [user?.id])

  useEffect(() => {
    if (loading) return
    const prev = prevAnnouncementsUnreadRef.current
    if (prev !== null && announcementsUnreadCount > prev) {
      setLiveRegionText(
        `${announcementsUnreadCount} unread announcement${announcementsUnreadCount !== 1 ? 's' : ''}`,
      )
      const timer = window.setTimeout(() => setLiveRegionText(''), 4000)
      prevAnnouncementsUnreadRef.current = announcementsUnreadCount
      return () => clearTimeout(timer)
    }
    prevAnnouncementsUnreadRef.current = announcementsUnreadCount
  }, [loading, announcementsUnreadCount])

  // Close on outside pointer / focus leaving the widget (non-modal dialog pattern)
  useEffect(() => {
    if (!isOpen) return

    const close = () => {
      setIsOpen(false)
      bellButtonRef.current?.focus()
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        close()
      }
    }

    const handleFocusIn = (event: FocusEvent) => {
      const t = event.target as Node | null
      if (t && containerRef.current && !containerRef.current.contains(t)) {
        close()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('focusin', handleFocusIn)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('focusin', handleFocusIn)
    }
  }, [isOpen])

  // Escape closes; Tab traps focus within the panel
  useEffect(() => {
    if (!isOpen) return
    const panel = panelRef.current
    if (!panel) return

    const focusables = () =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'))

    const focusFirst = () => {
      const els = focusables()
      const first = els[0]
      window.requestAnimationFrame(() => first?.focus())
    }
    focusFirst()

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setIsOpen(false)
        bellButtonRef.current?.focus()
        return
      }
      if (e.key !== 'Tab') return
      const els = focusables()
      if (els.length === 0) return
      const first = els[0]
      const last = els[els.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  const persistAnnouncementRead = async (notificationId: string) => {
    const uuid = announcementUuidFromNotificationId(notificationId)
    if (!uuid || !user) return
    try {
      await announcementsService.updateAnnouncementStates({ announcementId: uuid, read: true })
    } catch (err) {
      console.error('Failed to persist announcement read state:', err)
    }
  }

  const persistAnnouncementDismiss = async (notificationId: string) => {
    markLocallyDismissedAnnouncement(notificationId)
    const uuid = announcementUuidFromNotificationId(notificationId)
    if (!uuid || !user) return
    try {
      await announcementsService.updateAnnouncementStates({ announcementId: uuid, read: true, dismissed: true })
    } catch (err) {
      console.error('Failed to persist announcement dismiss:', err)
    }
  }

  const markAsRead = (id: string) => {
    void persistAnnouncementRead(id)
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n))
  }

  const markAllAsRead = () => {
    if (user) {
      const uuids = notifications
        .map((n) => announcementUuidFromNotificationId(n.id))
        .filter((x): x is string => Boolean(x))
      if (uuids.length > 0) {
        void announcementsService.updateAnnouncementStates({ announcementIds: uuids, read: true }).catch((err) => {
          console.error('Failed to mark announcements read:', err)
        })
      }
    }
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
  }

  const dismissNotification = (id: string) => {
    if (id.startsWith(DASHBOARD_ALERT_NOTIF_PREFIX)) {
      dismissDashboardAlert(id.slice(DASHBOARD_ALERT_NOTIF_PREFIX.length))
      setNotifications((prev) => prev.filter((notification) => notification.id !== id))
      return
    }
    void persistAnnouncementDismiss(id)
    setNotifications((prev) => prev.filter((notification) => notification.id !== id))
  }

  const dismissAllAnnouncements = async () => {
    const announcementIds = clearableServerAnnouncements.map((notification) => notification.id)
    if (announcementIds.length === 0) return

    const uuids = announcementIds
      .map(announcementUuidFromNotificationId)
      .filter((x): x is string => Boolean(x))
    for (const u of uuids) {
      pendingDismissedAnnouncementIdsRef.current.add(u)
    }
    setNotifications((prev) =>
      prev.filter((n) => {
        const uuid = announcementUuidFromNotificationId(n.id)
        return !uuid || !uuids.includes(uuid)
      }),
    )

    if (user && uuids.length > 0) {
      try {
        await announcementsService.updateAnnouncementStates({
          announcementIds: uuids,
          read: true,
          dismissed: true,
        })
      } catch (err) {
        console.error('Failed to dismiss announcements:', err)
        for (const u of uuids) {
          pendingDismissedAnnouncementIdsRef.current.delete(u)
        }
        void refetch()
      }
    }

    await refetch()
  }

  const handleNotificationClick = (notification: Notification) => {
    if (notification.type === 'dashboard_alert') {
      if (notification.link) {
        navigate(notification.link)
        setIsOpen(false)
        bellButtonRef.current?.focus()
      }
      return
    }

    markAsRead(notification.id)
    if ((notification.type === 'announcement' || notification.type === 'outage') && notification.linkedSet) {
      setActiveAnnouncementSet({
        linkedSet: notification.linkedSet,
        message: notification.message,
      })
      setIsOpen(false)
      bellButtonRef.current?.focus()
      return
    }

    if (notification.link) {
      navigate(notification.link)
      setIsOpen(false)
      bellButtonRef.current?.focus()
    }
  }

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'ticket_response':
        return <MessageSquare size={16} />
      case 'changelog':
        return <Sparkles size={16} />
      case 'announcement':
        return <Megaphone size={16} />
      case 'outage':
        return <AlertTriangle size={16} />
      case 'dashboard_alert':
        return <Megaphone size={16} />
      default:
        return <Bell size={16} />
    }
  }

  const getNotificationClass = (type: NotificationType) => {
    switch (type) {
      case 'ticket_response':
        return styles.typeTicket
      case 'changelog':
        return styles.typeChangelog
      case 'announcement':
        return styles.typeAnnouncement
      case 'outage':
        return styles.typeOutage
      case 'dashboard_alert':
        return styles.typeAnnouncement
      default:
        return ''
    }
  }

  const renderSeverityLabel = (severity?: AnnouncementSeverity) => {
    if (!severity) return null
    const label = severity === 'critical' ? 'CRITICAL' : severity === 'warning' ? 'WARNING' : 'INFO'
    const pillClass =
      severity === 'critical'
        ? styles.severityLabelCritical
        : severity === 'warning'
          ? styles.severityLabelWarning
          : styles.severityLabelInfo
    return (
      <span className={`${styles.severityLabel} ${pillClass}`}>
        {label}
      </span>
    )
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })
  }

  const toggleOpen = () => {
    setIsOpen((open) => {
      if (open) bellButtonRef.current?.focus()
      return !open
    })
  }

  return (
    <div className={styles.container} ref={containerRef}>
      <span className={styles.visuallyHidden} aria-live="polite" aria-atomic="true">
        {liveRegionText}
      </span>
      <button
        ref={bellButtonRef}
        type="button"
        className={`${styles.bellButton} ${badgeTotal > 0 ? styles.hasUnread : ''}`}
        onClick={toggleOpen}
        aria-label={`Notifications${badgeTotal > 0 ? `, ${badgeTotal} unread` : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-controls="notifications-popover"
      >
        <Bell size={20} />
        {badgeTotal > 0 && (
          <span className={styles.badge} aria-hidden="true">
            {badgeTotal > 9 ? '9+' : badgeTotal}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          id="notifications-popover"
          className={styles.dropdown}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notifications-popover-title"
        >
          <div className={styles.dropdownHeader}>
            <h3 id="notifications-popover-title">Notifications</h3>
            <div className={styles.headerActions}>
              {clearableServerAnnouncements.length > 0 && (
                <button
                  type="button"
                  className={styles.clearAnnouncements}
                  onClick={() => void dismissAllAnnouncements()}
                  aria-label="Clear announcements"
                >
                  <X size={14} />
                  Clear announcements
                </button>
              )}
              {announcementsUnreadCount > 0 && (
                <button 
                  type="button"
                  className={styles.markAllRead} 
                  onClick={markAllAsRead}
                  aria-label="Mark all as read"
                >
                  <CheckCheck size={14} />
                  Mark all read
                </button>
              )}
            </div>
          </div>

          <div className={styles.dropdownContent}>
            {unreadTicketCount > 0 && (
              <div className={styles.ticketSummaryStrip}>
                <button
                  type="button"
                  className={styles.ticketSummaryBtn}
                  onClick={() => {
                    navigate(isSuperAdmin ? '/super-admin/tickets' : '/support')
                    setIsOpen(false)
                    bellButtonRef.current?.focus()
                  }}
                >
                  <MessageSquare size={18} aria-hidden />
                  <span>
                    {isSuperAdmin
                      ? `${unreadTicketCount} unread ticket${unreadTicketCount !== 1 ? 's' : ''} (admin inbox)`
                      : `${unreadTicketCount} unread ticket response${unreadTicketCount !== 1 ? 's' : ''}`}
                  </span>
                </button>
              </div>
            )}
            {loading ? (
              <div className={styles.loading}>
                <div className={styles.spinner}></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className={styles.empty}>
                {unreadTicketCount === 0 && <Bell size={32} className={styles.emptyIcon} />}
                <p>{unreadTicketCount > 0 ? 'No announcements' : 'No notifications'}</p>
              </div>
            ) : (
              <ul className={styles.notificationList}>
                {notifications.map((notification) => (
                  <li key={notification.id}>
                    <div className={`${styles.notificationItem} ${!notification.read ? styles.unread : ''} ${getNotificationClass(notification.type)}`}>
                      <button
                        type="button"
                        className={styles.notificationMain}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className={styles.notificationIcon}>
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className={styles.notificationContent}>
                          <div className={styles.notificationTitle}>
                            {notification.title}
                            {renderSeverityLabel(notification.severity)}
                          </div>
                          <div className={styles.notificationMessage}>{notification.message}</div>
                          <div className={styles.notificationTime}>{formatTime(notification.createdAt)}</div>
                        </div>
                        {!notification.read && (
                          <div className={styles.unreadDot} aria-label="Unread"></div>
                        )}
                      </button>
                      {(notification.type === 'announcement' ||
                        notification.type === 'outage' ||
                        (notification.type === 'dashboard_alert' && notification.dashboardDismissible)) && (
                        <button
                          type="button"
                          className={styles.dismissButton}
                          onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            dismissNotification(notification.id)
                          }}
                          aria-label={`Dismiss ${notification.title}`}
                          title="Dismiss announcement"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.dropdownFooter}>
            <button 
              type="button"
              className={styles.viewAllBtn}
              onClick={() => {
                navigate(isSuperAdmin ? '/super-admin/tickets' : '/support')
                setIsOpen(false)
                bellButtonRef.current?.focus()
              }}
            >
              {user?.role === 'super_admin' ? 'View admin inbox' : 'View all tickets'}
            </button>
          </div>
        </div>
      )}

      <SetAnnouncementModal
        isOpen={Boolean(activeAnnouncementSet)}
        linkedSet={activeAnnouncementSet?.linkedSet || null}
        message={activeAnnouncementSet?.message || null}
        onClose={() => setActiveAnnouncementSet(null)}
      />
    </div>
  )
}

export default NotificationBell
